import { Box, FormControlLabel, Switch } from '@mui/material';
import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NShiftPartnersAutoComplete from './nshift-partners-auto-complete.component';
import BoxWithTitle from 'components/shared/box-with-title.component';
import { NShiftPartner } from 'models/nshift-partners';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useLazyQuery } from '@apollo/client';
import { nShiftPartnersTemplate } from 'apollo/templates/transport';
import NShiftPartnerAccordion from './nshift-partner-accordion/nshift-partner-accordion.component';
import { NShiftTransport, TransportPartner, TransportPartnerService } from 'models/organization';

type NShiftTransportSettingsProps = {
    nShiftTransport: NShiftTransport;
    onPartnersChange: (transportPartners: TransportPartner[]) => void;
    onToggleTransport: (isEnabled: boolean) => void;
};

const NShiftTransportSettings: FC<NShiftTransportSettingsProps> = ({
    nShiftTransport,
    onPartnersChange,
    onToggleTransport,
}: NShiftTransportSettingsProps) => {
    const { t: translate } = useTranslation('client-directory');
    const { showBoundary } = useDenthubErrorBoundary();
    const [isNShiftTransportEnabled, setIsNShiftTransportEnabled] = useState(false);
    const [selectedTransportPartners, setSelectedTransportPartners] = useState<NShiftPartner[]>([]);
    const [nShiftPartners, setNShiftPartners] = useState<NShiftPartner[]>([]);
    const [transportPartnersToSave, setTransportPartnersToSave] = useState<TransportPartner[]>([]);

    const [getNShiftPartners, { loading }] = useLazyQuery(nShiftPartnersTemplate, {
        onError: (err: Error) => {
            showBoundary(err);
        },
        onCompleted: ({ nShiftPartners }) => {
            const partners: NShiftPartner[] = [...nShiftPartners];
            if (nShiftTransport?.isEnabled && nShiftTransport.partners?.length) {
                const selectedPartners: NShiftPartner[] = [];
                for (const selectedPartner of nShiftTransport.partners) {
                    const index = partners.findIndex((partner) => partner.id === selectedPartner.id);
                    if (index > -1) {
                        selectedPartners.push(partners[index]);
                        partners.splice(index, 1);
                    }
                }
                setSelectedTransportPartners(selectedPartners);
            }
            setNShiftPartners(partners);
            setTransportPartnersToSave(nShiftTransport?.partners || []);
        },
    });

    useEffect(() => {
        if (nShiftTransport?.isEnabled) {
            setIsNShiftTransportEnabled(true);
            if (nShiftTransport.isEnabled) {
                getNShiftPartners();
            }
        }
    }, [nShiftTransport]);

    const toggleTransport = (event: ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        setIsNShiftTransportEnabled(isChecked);
        onToggleTransport(isChecked);
        if (isChecked) {
            getNShiftPartners();
        }
    };

    const handlePartnerSelect = (partner: NShiftPartner) => {
        const selectedPartners = [...selectedTransportPartners];
        selectedPartners.push(partner);
        setSelectedTransportPartners(selectedPartners);

        const partners = [...nShiftPartners];
        const partnerIndex = partners.findIndex((nShiftPartner) => {
            return nShiftPartner?.id === partner?.id;
        });

        if (partnerIndex > -1) {
            partners.splice(partnerIndex, 1);
            setNShiftPartners(partners);
        }
    };

    const handlePartnerDelete = (index: number) => {
        const selectedPartner = selectedTransportPartners[index];

        const selectedPartners = [...selectedTransportPartners];
        selectedPartners.splice(index, 1);
        setSelectedTransportPartners(selectedPartners);

        const partners = [...nShiftPartners];
        partners.push(selectedPartner);
        setNShiftPartners(partners);

        const savedPartners = [...transportPartnersToSave];
        savedPartners.splice(index, 1);
        setTransportPartnersToSave(savedPartners);
        onPartnersChange(savedPartners);
    };

    const handleServiceChange = (partner: NShiftPartner, services: TransportPartnerService[]) => {
        const transportPartners = [...transportPartnersToSave];
        const partnerIndex = transportPartners.findIndex((tPartner) => {
            return partner?.id === tPartner?.id;
        });
        const data = {
            id: partner.id,
            description: partner.description,
            services: services,
        };
        if (partnerIndex > -1) {
            transportPartners.splice(partnerIndex, 1, data);
        } else {
            transportPartners.push(data);
        }
        setTransportPartnersToSave(transportPartners);
        onPartnersChange(transportPartners);
    };

    return (
        <BoxWithTitle title={translate('transport:NSHIFT_TRANSPORT')}>
            <Box mb={1}>
                <FormControlLabel
                    control={
                        <Switch
                            color="secondary"
                            value={isNShiftTransportEnabled}
                            checked={isNShiftTransportEnabled}
                            onChange={toggleTransport}
                        />
                    }
                    label={translate('transport:ENABLE_TRANSPORT')}
                />
            </Box>
            {isNShiftTransportEnabled ? (
                <Box mb={2}>
                    <NShiftPartnersAutoComplete
                        nShiftPartners={nShiftPartners}
                        loading={loading}
                        onPartnerSelect={handlePartnerSelect}
                    />
                </Box>
            ) : undefined}

            {selectedTransportPartners.length
                ? selectedTransportPartners.map((transportPartner, index) => {
                      return (
                          <NShiftPartnerAccordion
                              key={index}
                              index={index}
                              selectedPartner={transportPartner}
                              onPartnerDelete={handlePartnerDelete}
                              onServiceChange={handleServiceChange}
                              savedPartner={transportPartnersToSave[index]}
                          />
                      );
                  })
                : undefined}
        </BoxWithTitle>
    );
};

export default NShiftTransportSettings;
